import { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useNotificationPreferences } from '@/hooks/useNotificationPreferences';
import { escucharAvisos } from '@/lib/notificationsRealtime';

export interface Notification {
  id: string;
  company_id: string | null;
  user_id: string | null;
  type: string | null;
  severity: string | null;
  title: string | null;
  message: string | null;
  data: Record<string, unknown> | null;
  read: boolean | null;
  created_at: string | null;
}

export function useNotifications() {
  const { user, company } = useAuth();
  const queryClient = useQueryClient();
  const [hasNewNotification, setHasNewNotification] = useState(false);
  const [latestNotification, setLatestNotification] = useState<Notification | null>(null);
  const { disabledTypes } = useNotificationPreferences();

  // Fetch notifications
  const { data: notifications = [], isLoading, error } = useQuery({
    queryKey: ['notifications', company?.id, user?.id],
    queryFn: async () => {
      if (!company?.id || !user?.id) return [];
      
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('company_id', company.id)
        .or(`user_id.eq.${user.id},user_id.is.null`)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      const notifs = data as Notification[];

      // Enrich notifications that reference a pest_id but lack pest_translation_key
      // (task_* notifications carrying pest_id in data)
      const TASK_TYPES_WITH_PEST = new Set(['task_completed', 'task_created', 'task_assigned', 'task_cancelled', 'task_due_soon']);
      const missingKey = notifs.filter(n => {
        const d = n.data as Record<string, unknown> | null;
        return n.type && TASK_TYPES_WITH_PEST.has(n.type) && d?.pest_id && !d?.pest_translation_key;
      });

      if (missingKey.length > 0) {
        const pestIds = [...new Set(missingKey.map(n => (n.data as Record<string, unknown>).pest_id as string))];
        const { data: pests } = await supabase
          .from('pests')
          .select('id, translation_key')
          .in('id', pestIds);

        if (pests && pests.length > 0) {
          const pestMap = new Map(pests.map(p => [p.id, p.translation_key]));
          for (const n of notifs) {
            const d = n.data as Record<string, unknown> | null;
            if (n.type && TASK_TYPES_WITH_PEST.has(n.type) && d?.pest_id && !d?.pest_translation_key) {
              const tk = pestMap.get(d.pest_id as string);
              if (tk) {
                n.data = { ...d, pest_translation_key: tk };
              }
            }
          }
        }
      }

      // Fallback enrichment by pest_name when pest_id is missing in data (e.g. task_completed notifications)
      const missingByName = notifs.filter(n => {
        const d = n.data as Record<string, unknown> | null;
        return n.type && TASK_TYPES_WITH_PEST.has(n.type)
          && d?.pest_name && typeof d.pest_name === 'string' && (d.pest_name as string).trim() !== ''
          && !d?.pest_translation_key && !d?.pest_id;
      });

      if (missingByName.length > 0) {
        const pestNames = [...new Set(missingByName.map(n => (n.data as Record<string, unknown>).pest_name as string))];
        const { data: pestsByName } = await supabase
          .from('pests')
          .select('name, translation_key')
          .in('name', pestNames);

        if (pestsByName && pestsByName.length > 0) {
          const nameMap = new Map(pestsByName.map(p => [p.name, p.translation_key]));
          for (const n of notifs) {
            const d = n.data as Record<string, unknown> | null;
            if (n.type && TASK_TYPES_WITH_PEST.has(n.type) && d?.pest_name && !d?.pest_translation_key && !d?.pest_id) {
              const tk = nameMap.get(d.pest_name as string);
              if (tk) {
                n.data = { ...d, pest_translation_key: tk };
              }
            }
          }
        }
      }

      // Filter out notifications whose type is disabled by user preferences
      return notifs.filter(n => !n.type || !(disabledTypes as Set<string>).has(n.type));
    },
    enabled: !!company?.id && !!user?.id,
    staleTime: 30000,
    /* Red de seguridad del tiempo real (A21/T04-03): la app entera tiene
       `refetchOnWindowFocus: false`, así que sin esto un aviso perdido no
       aparecía hasta recargar. Al volver a la ventana y cada minuto. */
    refetchOnWindowFocus: true,
    refetchInterval: 60000,
  });

  // Avisos en directo: UN canal por usuario para todas las copias de este hook
  // (auditoría del perito A21/T04-03; el porqué, en `notificationsRealtime.ts`).
  useEffect(() => {
    if (!company?.id || !user?.id) return;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const dejar = escucharAvisos(supabase, user.id, company.id, (nuevo) => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      setHasNewNotification(true);
      setLatestNotification(nuevo as unknown as Notification);
      clearTimeout(timer);
      timer = setTimeout(() => setHasNewNotification(false), 2000);
    });
    return () => {
      clearTimeout(timer);
      dejar();
    };
  }, [company?.id, user?.id, queryClient]);

  // Mark single notification as read
  const markAsRead = useMutation({
    mutationFn: async (notificationId: string) => {
      const { error } = await supabase
        .from('notifications')
        .update({ read: true })
        .eq('id', notificationId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  // Mark all notifications as read
  const markAllAsRead = useMutation({
    mutationFn: async () => {
      if (!company?.id || !user?.id) return;
      const { error } = await supabase
        .from('notifications')
        .update({ read: true })
        .eq('company_id', company.id)
        .or(`user_id.eq.${user.id},user_id.is.null`)
        .eq('read', false);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  // Delete a single notification
  const deleteNotification = useMutation({
    mutationFn: async (notificationId: string) => {
      const { error } = await supabase
        .from('notifications')
        .delete()
        .eq('id', notificationId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  // Delete all notifications for the current user/company
  const deleteAllNotifications = useMutation({
    mutationFn: async () => {
      if (!company?.id || !user?.id) return;
      const { error } = await supabase
        .from('notifications')
        .delete()
        .eq('company_id', company.id)
        .or(`user_id.eq.${user.id},user_id.is.null`);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const unreadCount = notifications.filter(n => !n.read).length;

  // Count unread by type
  const getUnreadCountByType = useCallback((type: string) => {
    return notifications.filter(n => !n.read && n.type === type).length;
  }, [notifications]);

  // Mark all notifications of a specific type as read
  const markTypeAsRead = useMutation({
    mutationFn: async (type: string) => {
      if (!company?.id || !user?.id) return;
      const unreadIds = notifications
        .filter(n => !n.read && n.type === type)
        .map(n => n.id);
      if (unreadIds.length === 0) return;
      const { error } = await supabase
        .from('notifications')
        .update({ read: true })
        .in('id', unreadIds);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const { t } = useTranslation();

  // Get relative time
  const getRelativeTime = useCallback((dateString: string | null) => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return t('notifications.time.now', 'ahora');
    if (diffMins < 60) return t('notifications.time.minutes', '{{count}}m', { count: diffMins });
    if (diffHours < 24) return t('notifications.time.hours', '{{count}}h', { count: diffHours });
    if (diffDays < 7) return t('notifications.time.days', '{{count}}d', { count: diffDays });
    return date.toLocaleDateString();
  }, [t]);

  const clearLatestNotification = () => {
    setLatestNotification(null);
  };

  return {
    notifications,
    isLoading,
    error,
    unreadCount,
    hasNewNotification,
    latestNotification,
    clearLatestNotification,
    markAsRead: markAsRead.mutate,
    markAllAsRead: markAllAsRead.mutate,
    deleteNotification: deleteNotification.mutate,
    deleteAllNotifications: deleteAllNotifications.mutate,
    isMarkingRead: markAsRead.isPending,
    isMarkingAllRead: markAllAsRead.isPending,
    isDeletingNotification: deleteNotification.isPending,
    isDeletingAll: deleteAllNotifications.isPending,
    getRelativeTime,
    getUnreadCountByType,
    markTypeAsRead: markTypeAsRead.mutate,
  };
}
